// client/src/components/KitchenSink.js
//
// Internal design-system reference. Renders every mischief primitive
// and shell component in isolation so we can eyeball them without
// having to navigate through a real screen. Deep-linkable at
// /kitchen-sink but NOT in the nav — this is a dev tool, not a user
// destination.
//
// As primitives get built, uncomment their imports + sections below.

import { Scrawl }    from './mischief/Scrawl';
import { Highlight } from './mischief/Highlight';
import { Sticker }   from './mischief/Sticker';
import { MarkerCircle }    from './mischief/MarkerCircle';
import { MarkerUnderline } from './mischief/MarkerUnderline';
import { MarkerArrow }     from './mischief/MarkerArrow';
import { PfShell } from './shell/PfShell';
import { PageTitle } from './shell/PageTitle';
import { PfTopBar } from './shell/PfTopBar';
import { PfTabBar } from './shell/PfTabBar';
import { useToast } from '../lib/ToastProvider';
import { useState } from 'react';
import { FormField } from './forms/FormField';
import { TextInput } from './forms/TextInput';
import { Textarea } from './forms/Textarea';
import { Select } from './forms/Select';
import { Checkbox } from './forms/Checkbox';
import { DateTimePicker } from './forms/DateTimePicker';
import { SubmitButton } from './forms/SubmitButton';
import { CascadingScopePicker } from './forms/CascadingScopePicker';


export function KitchenSink() {
  return (
    <div className="min-h-screen bg-bg px-6 py-10">
      <div className="max-w-2xl mx-auto">
        {/* Header — uses our tokens as a smoke test that Tailwind is
            actually picking them up. If "MISCHIEF KITCHEN" isn't
            uppercase-tracked-tiny and the divider isn't rule-grey,
            something's wrong with the config. */}
        <p className="text-eyebrow uppercase text-inkSub">Internal reference</p>
        <h1 className="font-serif text-pageTitle text-ink mt-2">
          Mischief kitchen
        </h1>
        <p className="text-inkSub mt-2 mb-8">
          Every primitive, in isolation, with sample copy.
        </p>
        <hr className="border-rule mb-10" />

        {/* ─── Scrawl ─────────────────────────────────────────── */}
        <Section
          title="Scrawl"
          desc='Caveat-script asides in the margins. Small, hand-drawn, always tilted a smidge.'
        >
          <div className="flex flex-wrap items-center gap-x-8 gap-y-6 py-4">
            <Scrawl>game day!</Scrawl>
            <Scrawl>← captain</Scrawl>
            <Scrawl color="accent">home game!</Scrawl>
            <Scrawl color="marker">practice at 6</Scrawl>
            <Scrawl color="red">running late</Scrawl>
            <Scrawl size="sm">brb</Scrawl>
            <Scrawl size="lg">GO BEARS</Scrawl>
            <Scrawl strikethrough color="inkSub">lose</Scrawl>
            <Scrawl rotate={-3}>fixed tilt: -3°</Scrawl>
            <Scrawl rotate={0}>flat (rotate=0)</Scrawl>
          </div>
        </Section>

        {/* ─── Highlight ──────────────────────────────────────── */}
        <Section
          title="Highlight"
          desc="Yellow marker strip behind a word. Slightly rotated so it feels drawn, not printed."
        >
          <div className="space-y-5 py-2">
            <p className="text-xl text-ink">
              He plays <Highlight>defense</Highlight> now.
            </p>
            <p className="text-xl text-ink">
              She scored <Highlight color="marker">three goals</Highlight> tonight.
            </p>
            <p className="text-xl text-ink">
              Practice runs from 6 to <Highlight color="accent">7:30pm</Highlight>.
            </p>
            <p className="text-lg text-inkSub">
              Inset variants:{' '}
              <Highlight inset="tight">tight</Highlight>,{' '}
              <Highlight inset="normal">normal</Highlight>,{' '}
              <Highlight inset="loose">loose</Highlight>.
            </p>
            <p className="text-3xl font-serif text-ink">
              Big serif <Highlight>headline</Highlight> word.
            </p>
          </div>
        </Section>

        {/* ─── Sticker ────────────────────────────────────────── */}
        <Section
          title="Sticker"
          desc="Tiny uppercase chip — HOME, AWAY, WIN, CUP. Tilted -5° to +5°, sits over other content."
        >
          <div className="space-y-6 py-2">
            {/* Row 1 — all tones on the paper background */}
            <div className="flex flex-wrap items-center gap-3">
              <Sticker>HOME</Sticker>
              <Sticker>AWAY</Sticker>
              <Sticker tone="win">WIN 4–2</Sticker>
              <Sticker tone="loss">LOSS 1–3</Sticker>
              <Sticker tone="tie">TIE 2–2</Sticker>
              <Sticker tone="accent">CUP</Sticker>
              <Sticker tone="marker">CAPTAIN</Sticker>
            </div>

            {/* Row 2 — fixed rotations to show the range */}
            <div className="flex flex-wrap items-center gap-3">
              <Sticker rotate={-5}>-5°</Sticker>
              <Sticker rotate={-3}>-3°</Sticker>
              <Sticker rotate={0}>0°</Sticker>
              <Sticker rotate={3}>+3°</Sticker>
              <Sticker rotate={5}>+5°</Sticker>
            </div>

            {/* Row 3 — the "absolute-positioned over a card" pattern.
                This is how stickers will most often appear in real
                screens: badging a card in the corner. */}
            <div className="relative bg-accentSoft rounded-card p-6 pr-14">
              <Sticker tone="win" className="absolute -top-2 right-3">
                WIN 5–1
              </Sticker>
              <p className="font-serif text-2xl text-ink">Polar Bears vs Puffins</p>
              <p className="text-inkSub text-sm mt-1">Sat · 10:30am · Home ice</p>
            </div>
          </div>
        </Section>

        {/* ─── MarkerCircle ───────────────────────────────────── */}
        <Section
          title="MarkerCircle"
          desc="Wobbly purple ellipse. Circles hero numbers, callouts, featured cards."
        >
          <div className="space-y-8 py-4">
            {/* Auto-sizing: circles fit whatever's inside */}
            <div className="flex flex-wrap items-center gap-x-10 gap-y-6">
              <span className="font-serif text-6xl text-ink">
                <MarkerCircle>7</MarkerCircle>
              </span>
              <span className="font-serif text-4xl text-ink">
                <MarkerCircle>W–L</MarkerCircle>
              </span>
              <span className="text-2xl text-ink">
                <MarkerCircle color="red">UPSET</MarkerCircle>
              </span>
              <span className="text-xl text-ink">
                <MarkerCircle color="accent">Featured</MarkerCircle>
              </span>
            </div>

            {/* Padding variants — same content, different breathing room */}
            <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
              <span className="text-xl text-ink">
                <MarkerCircle padding="tight">tight</MarkerCircle>
              </span>
              <span className="text-xl text-ink">
                <MarkerCircle padding="normal">normal</MarkerCircle>
              </span>
              <span className="text-xl text-ink">
                <MarkerCircle padding="loose">loose</MarkerCircle>
              </span>
            </div>

            {/* Stroke width variants */}
            <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
              <span className="text-xl text-ink">
                <MarkerCircle strokeWidth={1.5}>thin</MarkerCircle>
              </span>
              <span className="text-xl text-ink">
                <MarkerCircle strokeWidth={2.5}>default</MarkerCircle>
              </span>
              <span className="text-xl text-ink">
                <MarkerCircle strokeWidth={4}>bold</MarkerCircle>
              </span>
            </div>
          </div>
        </Section>

        {/* ─── MarkerUnderline ────────────────────────────────── */}
        <Section
          title="MarkerUnderline"
          desc="Curvy purple underline. Emphasizes a word without the visual weight of a full circle."
        >
          <div className="space-y-8 py-4">
            {/* Inside body copy — the most common usage */}
            <div className="space-y-4">
              <p className="text-xl text-ink">
                We <MarkerUnderline>defended</MarkerUnderline> the zone all night.
              </p>
              <p className="text-xl text-ink">
                Three <MarkerUnderline color="red">missed shots</MarkerUnderline> in the third.
              </p>
              <p className="text-xl text-ink">
                Look at <MarkerUnderline color="accent">Jamie</MarkerUnderline> go.
              </p>
            </div>

            {/* Standalone / headline scale */}
            <div className="space-y-3">
              <p className="font-serif text-4xl text-ink">
                <MarkerUnderline>Overtime</MarkerUnderline> winner.
              </p>
              <p className="text-3xl text-ink font-sans font-extrabold uppercase tracking-tight">
                <MarkerUnderline strokeWidth={3.5}>PENALTY</MarkerUnderline>
              </p>
            </div>

            {/* Offset variants — same word, three positions */}
            <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
              <span className="text-xl text-ink">
                <MarkerUnderline offset="close">close</MarkerUnderline>
              </span>
              <span className="text-xl text-ink">
                <MarkerUnderline offset="normal">normal</MarkerUnderline>
              </span>
              <span className="text-xl text-ink">
                <MarkerUnderline offset="far">far</MarkerUnderline>
              </span>
            </div>
          </div>
        </Section>

        {/* ─── MarkerArrow ────────────────────────────────────── */}
        <Section
          title="MarkerArrow"
          desc="Loopy purple arrow. Points at things — used on Practice's focus card."
        >
          <div className="space-y-10 py-4">
            {/* Pointing variant, all four directions */}
            <div>
              <p className="text-eyebrow uppercase text-inkSub mb-3">Pointing · directions</p>
              <div className="flex flex-wrap items-center gap-8">
                <MarkerArrow direction="left" />
                <MarkerArrow direction="right" />
                <MarkerArrow direction="up" />
                <MarkerArrow direction="down" />
              </div>
            </div>

            {/* Curly variant, all four directions */}
            <div>
              <p className="text-eyebrow uppercase text-inkSub mb-3">Curly · directions</p>
              <div className="flex flex-wrap items-center gap-8">
                <MarkerArrow variant="curly" direction="left" />
                <MarkerArrow variant="curly" direction="right" />
                <MarkerArrow variant="curly" direction="up" />
                <MarkerArrow variant="curly" direction="down" />
              </div>
            </div>

            {/* Size variants */}
            <div>
              <p className="text-eyebrow uppercase text-inkSub mb-3">Sizes</p>
              <div className="flex flex-wrap items-end gap-8">
                <MarkerArrow size="sm" />
                <MarkerArrow size="md" />
                <MarkerArrow size="lg" />
              </div>
            </div>

            {/* Color variants */}
            <div>
              <p className="text-eyebrow uppercase text-inkSub mb-3">Colors</p>
              <div className="flex flex-wrap items-center gap-8">
                <MarkerArrow color="marker" />
                <MarkerArrow color="red" />
                <MarkerArrow color="accent" />
              </div>
            </div>

            {/* The real usage: arrow + scrawl pointing at content */}
            <div>
              <p className="text-eyebrow uppercase text-inkSub mb-3">In context</p>
              <div className="relative bg-accentSoft rounded-card p-6 max-w-md">
                <p className="font-serif text-2xl text-ink">Focus this week</p>
                <p className="text-inkSub text-sm mt-1">Breakouts and zone exits</p>
                {/* Arrow points from the scrawl to the card */}
                <div className="absolute -top-4 -right-12 flex items-center gap-1">
                  <span className="font-scrawl text-xl text-marker">work on this</span>
                  <MarkerArrow direction="left" size="sm" />
                </div>
              </div>
            </div>
          </div>
        </Section>
        {/* ─── PageTitle ──────────────────────────────────────── */}
        <Section
          title="PageTitle"
          desc="Giant Instrument Serif screen anchor. Optional eyebrow above, optional garnish beside."
        >
          <div className="space-y-10 py-4">
            {/* Bare title */}
            <PageTitle>Schedule</PageTitle>

            {/* With eyebrow */}
            <PageTitle eyebrow="This season">Stats</PageTitle>

            {/* With a Highlight inside the title */}
            <PageTitle eyebrow="Your team">
              <Highlight>Roster</Highlight>
            </PageTitle>

            {/* With a scrawl garnish */}
            <PageTitle
              eyebrow="This week"
              garnish={<Scrawl color="marker">← focus</Scrawl>}
            >
              Practice
            </PageTitle>

            {/* Kitchen sink of composition — highlight + arrow + scrawl */}
            <PageTitle
              eyebrow="Playoffs"
              garnish={
                <span className="flex items-center gap-1">
                  <Scrawl color="marker">win it</Scrawl>
                  <MarkerArrow direction="left" size="sm" />
                </span>
              }
            >
              <Highlight color="marker">Bracket</Highlight>
            </PageTitle>
          </div>
        </Section>
        {/* ─── PfTopBar ───────────────────────────────────────── */}
        <Section
          title="PfTopBar"
          desc="Team crest, name, tier on the left. Role pill on the right. Slim 56px chrome."
        >
          <div className="space-y-3 py-2">
            {/* Standalone TopBars, wrapped in a mini phone-width frame
                so they behave visually the way they will inside Shell. */}
            <div className="relative h-14 max-w-md border border-rule rounded-lg overflow-hidden">
              <PfTopBar
                teamName="Coastline Polar Bears"
                tier="U12 AA · 25–26"
                role="PARENT"
              />
            </div>

            <div className="relative h-14 max-w-md border border-rule rounded-lg overflow-hidden">
              <PfTopBar
                teamName="Jr. Polar Bears Black"
                tier="U14 A · 25–26"
                role="COACH"
              />
            </div>

            <div className="relative h-14 max-w-md border border-rule rounded-lg overflow-hidden">
              <PfTopBar
                teamName="Learn To Play"
                tier="Beginner · Fall 25"
                role="PLAYER"
              />
            </div>

            {/* Long name to prove the truncation works */}
            <div className="relative h-14 max-w-md border border-rule rounded-lg overflow-hidden">
              <PfTopBar
                teamName="Extremely Long Team Name That Should Truncate"
                tier="U16 AAA · 25–26"
                role="PARENT"
              />
            </div>
          </div>
        </Section>
        {/* ─── PfTabBar ───────────────────────────────────────── */}
        <Section
          title="PfTabBar"
          desc="Bottom nav. 3 tabs. Active state comes from the URL — try navigating between /teams/demo/schedule, /teams/demo/team, /teams/demo/practice."
        >
          {/* Standalone preview inside a mini phone-width frame so
              positioning behaves the way it will in PfShell. */}
          <div className="relative h-[110px] max-w-md border border-rule rounded-lg overflow-hidden">
            <PfTabBar teamId="demo" />
          </div>

          <p className="text-inkSub text-sm mt-4">
            Note: active tab shows only when the current URL matches one of{' '}
            <code className="text-ink">/teams/demo/&lt;tab&gt;</code>. On this
            kitchen sink page, no tab is active.
          </p>
        </Section>
        {/* ─── PfShell (preview) ──────────────────────────────── */}
        <Section
          title="PfShell"
          desc="Mobile-frame wrapper. Both TopBar and TabBar wired up. This is what the team-detail chrome looks like."
        >
        {/* ─── Toast ──────────────────────────────────────────── */}
        <Section
        title="Toast"
        desc="App-level notification chip. Slightly tilted, tone-matched left stripe, tap to dismiss, auto-dismisses after 4s. Fires from anywhere via useToast()."
      >
      <ToastDemo />
      </Section>

        {/* ─── Form primitives (text entry) ─────────────────── */}
        <Section
          title="Form primitives · text entry"
          desc="FormField wraps the workhorses (TextInput, Textarea, Select). Label, helper nudge, and error slot are FormField's job; each input stays a bare chip."
        >
          <FormPrimitivesDemo />
        </Section>

        {/* ─── Form primitives · interactive controls ─────────── */}    
        <Section
          title="Form primitives · interactive controls"
          desc="Checkbox has its own layout (label inline, no FormField wrapper). DateTimePicker matches TextInput visually. SubmitButton is the primary action — full-width, ink outline, hover-inverts."
        >
          <FormControlsDemo />
        </Section>

        {/* ─── CascadingScopePicker ───────────────────────────── */}
        <Section
          title="CascadingScopePicker"
          desc="Composed picker for coach-side forms. Segmented control → dependent selects. Emits the shape POST /announcements expects."
        >
          <CascadingScopePickerDemo />
        </Section>


          {/* We display PfShell inside a bordered demo area so its
              phone-width constraint is visible on desktop. This is
              NOT how it will be mounted in real routes — normally it
              renders at page root. */}
          <div className="border border-dashed border-rule rounded-lg overflow-hidden">
            <PfShell
              topBar={
                <PfTopBar
                  teamName="Coastline Polar Bears"
                  tier="U12 AA · 25–26"
                  role="PARENT"
                />
              }
              tabBar={<PfTabBar teamId="demo" />}
            >
              <div className="py-8">
                <p className="text-eyebrow uppercase text-inkSub mb-2">
                  Screen content area
                </p>
                <p className="font-serif text-4xl text-ink mb-4">
                  Your screen goes here
                </p>
                <p className="text-inkSub">
                  Padded with <code className="text-ink">screenX</code>{' '}
                  (22px) on both sides. Top and bottom clearance reserved
                  for chrome.
                </p>
              </div>
            </PfShell>
          </div>

          {/* Second preview: chrome disabled. Shows what login/onboarding
              screens will look like — no top or bottom bars. */}
          <p className="text-eyebrow uppercase text-inkSub mt-8 mb-3">
            showChrome=false
          </p>
          <div className="border border-dashed border-rule rounded-lg overflow-hidden">
            <PfShell showChrome={false}>
              <div className="py-16 text-center">
                <p className="font-serif text-3xl text-ink">No chrome</p>
                <p className="text-inkSub mt-2">
                  Used for pre-auth or org-picker screens.
                </p>
              </div>
            </PfShell>
          </div>
        </Section>
      </div>
    </div>
  );
}

function ToastDemo() {
  const toast = useToast();
  return (
    <div className="flex flex-col gap-2 items-start">
      <button
        type="button"
        onClick={() => toast.success('Announcement posted')}
        className="px-3 py-1.5 bg-paper border border-rule rounded-md text-sm"
      >
        Fire success
      </button>
      <button
        type="button"
        onClick={() => toast.error('Could not save — try again')}
        className="px-3 py-1.5 bg-paper border border-rule rounded-md text-sm"
      >
        Fire error
      </button>
      <button
        type="button"
        onClick={() => toast.info('Draft restored from last session')}
        className="px-3 py-1.5 bg-paper border border-rule rounded-md text-sm"
      >
        Fire info
      </button>
      <button
        type="button"
        onClick={() => {
          toast.success('First');
          setTimeout(() => toast.info('Second'), 300);
          setTimeout(() => toast.error('Third'), 600);
        }}
        className="px-3 py-1.5 bg-ink text-paper rounded-md text-sm"
      >
        Fire three (stacking)
      </button>
    </div>
  );
}

function FormPrimitivesDemo() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [importance, setImportance] = useState('Normal');

  return (
    <div className="space-y-6 py-2">
      {/* Full composition: label + input + helper */}
      <FormField
        label="Title"
        helperText="Keep it short — people scan headlines"
      >
        <TextInput
          value={title}
          onChange={setTitle}
          placeholder="Rink 2 closed Saturday"
          maxLength={80}
        />
      </FormField>

      {/* Error state */}
      <FormField
        label="Title (with error)"
        helperText="This helper is hidden while an error is showing"
        error="Title is required"
      >
        <TextInput value="" onChange={() => {}} placeholder="Empty on purpose" />
      </FormField>

      {/* Textarea */}
      <FormField
        label="Body"
        helperText="Explain the what, when, and where"
      >
        <Textarea
          value={body}
          onChange={setBody}
          rows={5}
          placeholder="Rink 2 will be closed Saturday for resurfacing. Practice moved to Rink 1 at the same time."
        />
      </FormField>

      {/* Select */}
      <FormField label="Importance">
        <Select value={importance} onChange={setImportance}>
          <option value="Normal">Normal</option>
          <option value="High">High</option>
          <option value="Urgent">Urgent</option>
          <option value="Low">Low</option>
        </Select>
      </FormField>

      

      {/* Disabled state */}
      <FormField label="Disabled">
        <TextInput
          value="Read only"
          onChange={() => {}}
          disabled
        />
      </FormField>
    </div>
  );
}

function FormControlsDemo() {
  const [pinned, setPinned] = useState(false);
  const [notify, setNotify] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(false);

  return (
    <div className="space-y-8 py-2">
      {/* Checkbox — the four state combos */}
      <div className="space-y-3">
        <p className="text-eyebrow uppercase text-inkSub mb-2">Checkbox</p>
        <Checkbox
          label="Pin this announcement"
          checked={pinned}
          onChange={setPinned}
        />
        <Checkbox
          label="Notify parents by email"
          checked={notify}
          onChange={setNotify}
        />
        <Checkbox
          label="Disabled + checked"
          checked
          onChange={() => {}}
          disabled
        />
        <Checkbox
          label="Disabled + unchecked"
          checked={false}
          onChange={() => {}}
          disabled
        />
      </div>

      {/* DateTimePicker — inside a FormField like real usage */}
      <div>
        <p className="text-eyebrow uppercase text-inkSub mb-2">DateTimePicker</p>
        <FormField
          label="Expires at"
          helperText="Leave empty and pinned announcements auto-expire in 24h"
        >
          <DateTimePicker value={expiresAt} onChange={setExpiresAt} />
        </FormField>
      </div>

      {/* SubmitButton — three states */}
      <div className="space-y-3">
        <p className="text-eyebrow uppercase text-inkSub mb-2">Submit button</p>
        <SubmitButton>Post announcement</SubmitButton>
        <SubmitButton disabled>Disabled</SubmitButton>
        <SubmitButton
          loading={loading}
          onClick={(e) => {
            e.preventDefault();
            setLoading(true);
            setTimeout(() => setLoading(false), 2000);
          }}
        >
          Click to see loading state
        </SubmitButton>
      </div>
    </div>
  );
}

function CascadingScopePickerDemo() {
  const [value, setValue] = useState({ scope: 'org' });
  return (
    <div className="space-y-6">
      <CascadingScopePicker value={value} onChange={setValue} />
      <div>
        <p className="text-eyebrow uppercase text-inkSub mb-2">
          Emitted value (what POST /announcements will receive)
        </p>
        <pre className="text-xs bg-bg border border-rule rounded-md p-3 text-ink overflow-x-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    </div>
  );
}

// A section header + description + demo slot. Kept local because it's
// only ever used inside this file.
function Section({ title, desc, children }) {
  return (
    <section className="mb-12">
      <h2 className="font-serif text-3xl text-ink">{title}</h2>
      <p className="text-inkSub text-sm mt-1 mb-4">{desc}</p>
      <div className="bg-paper rounded-card p-6 border border-rule">
        {children}
      </div>
    </section>
  );
}

// Grey dashed box that says "___ goes here" — used until the real
// primitive exists. Delete each PlaceholderSlot as its primitive
// comes online.
function PlaceholderSlot({ label }) {
  return (
    <div className="border-2 border-dashed border-rule rounded-lg py-8 text-center text-inkSub text-sm">
      {label}
    </div>
  );
}